import { Channel, Program } from '../types'
import { useProgramsPerDay } from '../hooks'
import { ProgramCard } from './program-card'
import { useApp } from './app-context'
import { useEffect, useRef } from 'react'

interface ProgramProps {
  daysInterval?: number
  timelineInterval?: number
  channel: Channel
  day: string
  onProgramClick: (
    id: number | undefined,
    channel: Channel | undefined,
    date?: string | undefined
  ) => void
  setCurrentInfo: React.Dispatch<
    React.SetStateAction<{
      ref: React.RefObject<HTMLLIElement | null>
      idx: number
    } | null>
  >
  dateInView: Date
}

export const ChannelDayRow = ({
  channel,
  day,
  onProgramClick,
  setCurrentInfo,
  dateInView,
}: ProgramProps) => {
  const { data } = useProgramsPerDay(channel.callLetter, day, true)
  const { isProgramRecording } = useApp()
  const rowRef = useRef<HTMLLIElement>(null)

  const isSelectedDate = day === dateInView.toISOString().slice(0, 10)
  const currentDate = new Date()
  const isCurrentDate = day === currentDate.toISOString().slice(0, 10)

  useEffect(() => {
    if (isSelectedDate && data) {
      // Find the current program (the one that is currently airing)
      const now = new Date()
      const checkDate = new Date(now)
      checkDate.setFullYear(dateInView.getFullYear())
      checkDate.setMonth(dateInView.getMonth())
      checkDate.setDate(dateInView.getDate())

      //? Delay here is to ensure all rows were rendered
      setTimeout(() => {
        const currentProgramIdx = data.findIndex((program: Program) => {
          return checkDate >= program.start.date && checkDate < program.end.date
        })
        if (currentProgramIdx !== -1) {
          setCurrentInfo({
            ref: rowRef,
            idx: currentProgramIdx,
          })
          const cardWrapper = rowRef.current?.querySelectorAll(
            '.program-card-wrapper'
          )[currentProgramIdx] as HTMLElement

          if (cardWrapper) {
            const scrollContainer = rowRef.current?.closest(
              '.channel-day-container'
            ) as HTMLElement

            if (scrollContainer) {
              requestAnimationFrame(() => {
                const cardRect = cardWrapper.getBoundingClientRect()
                const containerRect = scrollContainer.getBoundingClientRect()

                const cardLeftRelativeToContainer =
                  cardRect.left - containerRect.left
                const currentScrollLeft = scrollContainer.scrollLeft

                const cardWidth = cardRect.width
                const containerWidth = containerRect.width
                const scrollLeft =
                  currentScrollLeft +
                  cardLeftRelativeToContainer -
                  containerWidth / 2 +
                  cardWidth / 2
                //! Dont remove the setTimeout. Rare issues on mobile without it
                setTimeout(() => {
                  scrollContainer.scrollTo({
                    left: scrollLeft,
                    behavior: 'instant',
                  })
                }, 100)
              })
            } else {
              cardWrapper.scrollIntoView({
                inline: 'center',
                block: 'nearest',
                behavior: 'instant',
              })
            }
          } else {
            console.error('Card wrapper not found for index:', currentProgramIdx)
          }
        } else {
          console.error('No current program found in data')
        }
      }, 2000)
    }
  }, [isSelectedDate, data, setCurrentInfo, dateInView, isCurrentDate])

  return (
    <li ref={rowRef} className="channel-day-row">
      {data?.map((program: Program, index: number) => (
        <ProgramCard
          key={`${program.programId}-${index}`}
          program={program}
          channel={channel}
          recordingType={isProgramRecording(program.programId)}
          onProgramClick={onProgramClick}
          displayNow={
            program.start.date <= currentDate && program.end.date >= currentDate
          }
          hiddeChannelTitle
        />
      ))}
    </li>
  )
}
